import React, { useState, useMemo } from 'react';
import {
  Shield, Layers, AlertTriangle, Info
} from 'lucide-react';
import { tiers } from '../data/tiersData';
import { formatCurrency } from '../utils/formatters';

const TiersPage = () => {
  const [input, setInput] = useState('');

  const parsedVal = parseInt(input.replace(/[^0-9]/g, ''), 10) || 0;

  const matchedTier = useMemo(() => {
    if (parsedVal < 500) return null;
    return tiers.find(t => parsedVal >= t.minAccountSize && parsedVal <= t.maxAccountSize) || null;
  }, [parsedVal]);

  const formatInput = (val) => {
    const digits = val.replace(/[^0-9]/g, '');
    if (!digits) return '';
    return '$' + Number(digits).toLocaleString();
  };

  const riskPct = matchedTier ? ((matchedTier.maxLoss / parsedVal) * 100).toFixed(1) : 0;

  return (
    <div className="page">
      <div className="ct">
        <div className="ct-header">
          <h1 className="ct-header__title">Sizing</h1>
          <p className="ct-header__sub">
            Enter your account size and we'll tell you exactly how to trade.
          </p>
        </div>

        {/* Account size input */}
        <div className="ct-input-card">
          <div className="ct-input__top">
            <label className="ct-input__label">
              Account Size
            </label>
            {parsedVal > 0 && parsedVal < 500 && (
              <span className="ct-input__warn">
                <AlertTriangle size={12} />
                Minimum $500
              </span>
            )}
          </div>
          <div className="ct-input__wrap">
            <span className="ct-input__prefix">$</span>
            <input
              type="text"
              className="ct-input__field"
              placeholder="10,000"
              value={input.replace(/^\$/, '')}
              onChange={e => setInput(formatInput(e.target.value))}
            />
          </div>
        </div>

        {/* Results */}
        {matchedTier && (
          <div className="ct-results">
            <div className="ct-results__badge">Tier {matchedTier.tier}</div>

            {/* Recommendation cards */}
            <div className="ct-rec-grid">
              <div className="ct-rec">
                <div className="ct-rec__icon ct-rec__icon--cyan">
                  <Layers size={20} />
                </div>
                <div className="ct-rec__body">
                  <span className="ct-rec__label">Contracts to Trade</span>
                  <span className="ct-rec__value">{matchedTier.maxContracts} MNQ</span>
                </div>
              </div>

              <div className="ct-rec">
                <div className="ct-rec__icon ct-rec__icon--red">
                  <Shield size={20} />
                </div>
                <div className="ct-rec__body">
                  <span className="ct-rec__label">Max Loss Per Trade</span>
                  <span className="ct-rec__value ct-rec__value--red">{formatCurrency(matchedTier.maxLoss)}</span>
                </div>
              </div>
            </div>

            {/* Explanation */}
            <div className="ct-explain">
              <div className="ct-explain__icon">
                <Info size={16} />
              </div>
              <div className="ct-explain__text">
                <p className="ct-explain__title">Why these numbers?</p>
                <p className="ct-explain__body">
                  With a {formatCurrency(parsedVal)} account, your max loss of {formatCurrency(matchedTier.maxLoss)} per trade
                  represents <strong>{riskPct}%</strong> of your total capital. This keeps your risk controlled so that
                  a single losing trade never significantly damages your account. Trading {matchedTier.maxContracts} contract{matchedTier.maxContracts > 1 ? 's' : ''} at
                  this level ensures you're properly sized — large enough to make meaningful gains on winners,
                  but small enough that drawdowns remain manageable.
                </p>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default TiersPage;
